import React, {
    useState,
    useCallback,
    useMemo,
    memo,
    useImperativeHandle,
    forwardRef,
    useEffect,
    useRef,
} from 'react';

import { Empty, message } from 'antd';

import { wrapFormField } from '~/helpers/form-helper';
import useAxios from '~/hooks/use-axios';

import Input from '../Input';
import requestCities from './request-cities';
import requestCompanies from './request-companies';
import requestCountries from './request-countries';
import requestDistanceEstimator from './request-distance-estimator';
import requestEmployees from './request-employees';
import requestEmployeesGroups from './request-employees-groups';
import requestEnclosures from './request-enclosures';
import requestHosts from './request-hosts';
import requestImageSources from './request-image-sources';
import requestSourceLocation from './request-source-location';
import requestUserRoles from './request-user-roles';

const LIMIT = 15;
const INITIAL_PAGE = 1;

const useRequestByType = searchType => {
    return useMemo(() => {
        switch (searchType) {
            case 'cities':
                return requestCities;
            case 'employees':
                return requestEmployees;
            case 'groups':
                return requestEmployeesGroups;
            case 'enclosures':
                return requestEnclosures;
            case 'countries':
                return requestCountries;
            case 'userRoles':
                return requestUserRoles;
            case 'companies':
                return requestCompanies;
            case 'imageSources':
                return requestImageSources;
            case 'sourceLocation':
                return requestSourceLocation;
            case 'distanceEstimator':
                return requestDistanceEstimator;
            case 'hosts':
                return requestHosts;
            default:
                return Promise.resolve;
        }
    }, [searchType]);
};

const requestOptions = async ({
    axios,
    requestSearch,
    searchValue,
    page,
    dataSource,
    params,
}) => {
    try {
        const response = await requestSearch(
            axios,
            searchValue,
            page,
            LIMIT,
            params,
        );
        const { result, metadata } = response;

        return {
            dataSource: [...dataSource, ...result],
            canLoadMore: metadata.page < metadata.pages,
            page: metadata.page,
        };
    } catch (err) {
        console.warn(err);
        if (err.response?.status === 404) {
            return {
                dataSource: [],
                canLoadMore: false,
                page: INITIAL_PAGE,
            };
        }

        if (err.response) {
            message.error(
                'Não foi possível carregar as opções, tente novamente mais tarde.',
            );
        } else {
            message.error(
                'Você parece estar offline, verifique sua conexão com a Internet.',
            );
        }
        throw err;
    }
};

const InputSearch = memo(
    forwardRef(
        (
            {
                input, params, searchType, onDropdownVisibleChange, ...others
            },
            ref,
        ) => {
            const axios = useAxios();
            const requestTimeout = useRef();
            const requestSearch = useRequestByType(searchType);

            const [fetching, setFetching] = useState(false);
            const [dataSource, setDataSource] = useState([]);
            const [canLoadMore, setCanLoadMore] = useState(false);
            const [page, setPage] = useState(0);
            const [searchValue, setSearchValue] = useState('');

            useImperativeHandle(
                ref,
                () => ({
                    clearOptions: () => {
                        setDataSource([]);
                        setCanLoadMore(false);
                        setPage(0);
                    },
                }),
                [],
            );

            useEffect(() => {
                return () => {
                    clearTimeout(requestTimeout.current);
                };
            }, []);

            const handleSearch = useCallback(
                value => {
                    setFetching(true);

                    clearTimeout(requestTimeout.current);
                    requestTimeout.current = setTimeout(async () => {
                        const result = await requestOptions({
                            axios,
                            dataSource: [],
                            page: INITIAL_PAGE,
                            searchValue: value,
                            requestSearch,
                            params,
                        });

                        setDataSource(result.dataSource);
                        setCanLoadMore(result.canLoadMore);
                        setPage(result.page);
                        setFetching(false);
                    }, 200);

                    setDataSource([]);
                    setCanLoadMore(false);
                    setPage(INITIAL_PAGE);
                    setSearchValue(value);
                },
                [requestSearch, params, axios],
            );

            const handleLoadMore = useCallback(async () => {
                setFetching(true);

                const nextPage = page + 1;

                const result = await requestOptions({
                    axios,
                    dataSource,
                    page: nextPage,
                    searchValue,
                    requestSearch,
                    params,
                });

                setDataSource(result.dataSource);
                setCanLoadMore(result.canLoadMore);
                setPage(result.page);
                setFetching(false);
            }, [searchValue, dataSource, page, requestSearch, params, axios]);

            const handleDropdownVisibleChange = useCallback(
                visible => {
                    if (onDropdownVisibleChange) onDropdownVisibleChange(visible);

                    if (visible && !dataSource.length) {
                        handleSearch('');
                    }
                },
                [onDropdownVisibleChange, dataSource, handleSearch],
            );

            const inputProps = useMemo(
                () => ({
                    ...input,
                    type: 'select',
                }),
                [input],
            );

            return (
                <Input
                    {...others}
                    onSearch={handleSearch}
                    options={dataSource}
                    input={inputProps}
                    loading={fetching}
                    showSearch
                    notFoundContent={<Empty description={false} />}
                    onLoadMoreClick={canLoadMore ? handleLoadMore : null}
                    onDropdownVisibleChange={handleDropdownVisibleChange}
                    filterOption={false}
                />
            );
        },
    ),
);

InputSearch.Field = wrapFormField(InputSearch);

export default InputSearch;
